import type { CSSProperties } from 'react';

export interface StoreBrandingInput {
  logoUrl?: string;
  bannerUrl?: string;
  primaryColor?: string;
  cardStyle?: string;
  radius?: number;
}
export interface ValidatedBranding {
  logoUrl?: string;
  bannerUrl?: string;
  primaryColor: string;
  cardStyle: 'elevated' | 'flat' | 'outlined';
  radius: number;
  style: CSSProperties;
}

const fallback = { primaryColor: '#0f3d2e', cardStyle: 'elevated' as const, radius: 18 };

function safeAsset(
  value: string | undefined,
  allowedOrigins: ReadonlySet<string>
): string | undefined {
  if (!value || typeof window === 'undefined') return undefined;
  try {
    const url = new URL(value, window.location.origin);
    const sameOrigin = url.origin === window.location.origin;
    if (!sameOrigin && (url.protocol !== 'https:' || !allowedOrigins.has(url.origin)))
      return undefined;
    if (url.username || url.password) return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

function contrastWithWhite(hex: string): number {
  const channels = [1, 3, 5]
    .map((index) => Number.parseInt(hex.slice(index, index + 2), 16) / 255)
    .map((value) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  return 1.05 / (luminance + 0.05);
}

export function validateBranding(
  input?: StoreBrandingInput | null,
  allowedAssetOrigins: readonly string[] = []
): ValidatedBranding {
  const origins = new Set(
    allowedAssetOrigins.flatMap((value) => {
      try {
        return [new URL(value).origin];
      } catch {
        return [];
      }
    })
  );
  const candidate =
    typeof input?.primaryColor === 'string' &&
    /^#[0-9a-f]{6}$/i.test(input.primaryColor) &&
    contrastWithWhite(input.primaryColor) >= 4.5
      ? input.primaryColor.toLowerCase()
      : fallback.primaryColor;
  const radius =
    Number.isInteger(input?.radius) && input!.radius! >= 8 && input!.radius! <= 28
      ? input!.radius!
      : fallback.radius;
  const cardStyle =
    input?.cardStyle === 'flat' ||
    input?.cardStyle === 'outlined' ||
    input?.cardStyle === 'elevated'
      ? input.cardStyle
      : fallback.cardStyle;
  return {
    logoUrl: safeAsset(input?.logoUrl, origins),
    bannerUrl: safeAsset(input?.bannerUrl, origins),
    primaryColor: candidate,
    cardStyle,
    radius,
    style: {
      '--customer-color-brand': candidate,
      '--customer-radius-md': `${radius}px`,
    } as CSSProperties,
  };
}
